import React from 'react';
import { Dimensions, Platform, TouchableOpacity } from 'react-native';

import { Badge, Card, VFText, Icon, IconTypes } from '@vfgroup-oneplatform/foundation/Components';
import { ShimmerWrapper } from '@vfgroup-oneplatform/foundation/Components/Shimmer';
import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import ContentManager from '@vfgroup-oneplatform/foundation/ContentManager';
import { dateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils/dateCurrencyFormate';
import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import { billCardLoadingConfig } from '../../Common/LoadingConfigurations/BillCardLoading/BillCardLoading.config';
import { getThemeImages } from '../../Themes';
import { ServiceToImageMapper, DateFormatters } from '../../Utils';
import BillingTestKeys from '../../Utils/BillingTestKeys.js';
import ImageList from '../ImageList';
import Tag from '../Tag';
import VfView from '../VfView';

import styles from './BillCard.Styles';

function getDateFormatters(getTimePeriod, getYear, getMonth) {
  const _getTimePeriod = _isFunction(getTimePeriod) ? getTimePeriod : DateFormatters.getTimePeriod;
  const _getYear = _isFunction(getYear) ? getYear : DateFormatters.getYear;
  const _getMonth = _isFunction(getMonth) ? getMonth : DateFormatters.getMonth;

  return {
    getTimePeriod: _getTimePeriod,
    getYear: _getYear,
    getMonth: _getMonth,
  };
}

const getDateFormattersMemoized = memoize(getDateFormatters);

const getBillAmountStyle = (withNegativeSignForNegativeAmount, billAmountStyle) =>
  withNegativeSignForNegativeAmount ? billAmountStyle : undefined;

const getBillAmountWithCurrencyFirst = (isCurrencyFirst, withBillNegativeAmount, value, currency) =>
  isCurrencyFirst
    ? currency
    : dateCurrencyFromatter.currencyTransformer(withBillNegativeAmount ? value : Math.abs(value));

const getBillAmountWithAmountFirst = (isCurrencyFirst, withBillNegativeAmount, value, currency) =>
  isCurrencyFirst
    ? dateCurrencyFromatter.currencyTransformer(withBillNegativeAmount ? value : Math.abs(value))
    : currency;

function BillCard({
  isLoading,
  loader,
  imageListProp,
  descriptionFirstLine,
  descriptionFirstLineStyle,
  unPaidCardDescriptionText,
  unPaidCardDescriptionTextStyle,
  unPaidCardDescriptionTextPlaceholders,
  descriptionSecondLine,
  descriptionSecondLineStyle,
  renderFooter,
  isCurrencyFirst,
  value,
  currency,
  onPress,
  status,
  startDate,
  endDate,
  isStartDateBillDate,
  getYear,
  getMonth,
  getTimePeriod,
  paidValue,
  theme,
  tagContainerStyle,
  tagTextStyle,
  monthTestID,
  amountTestID,
  priceTestID,
  withTimePeriodDisplayed,
  withServicesExtraLabel,
  servicesExtraLabel,
  billCardTestID,
  issueDate,
  tagText,
  withBillNegativeAmount,
  billAmountStyle,
  toggleModal,
  billingNegativeValueNote,
  billingNegativeValueNoteStyle,
}) {
  const Loader = loader ? (
    loader
  ) : (
    <ShimmerWrapper isLoading={isLoading} loadingConfig={billCardLoadingConfig} />
  );

  const images = ServiceToImageMapper(imageListProp, theme.name);

  const dataFormatters = getDateFormattersMemoized(getTimePeriod, getYear, getMonth);

  const billDate = issueDate ? issueDate : isStartDateBillDate ? startDate : endDate;
  const year = dataFormatters.getYear(billDate);
  const month = dataFormatters.getMonth(billDate);
  const timePeriod = dataFormatters.getTimePeriod(startDate, endDate);
  const remainingAmountPlaceholders = [
    dateCurrencyFromatter.currencyTransformer(paidValue),
    dateCurrencyFromatter.currencyTransformer(value - paidValue),
  ];
  const remainingAmountText = ContentManager.translate(
    'billing_details_partially_paid_remaining',
    remainingAmountPlaceholders,
  ).split(' (');
  const onNegativeBillIconPress = () => {
    toggleModal();
  };
  const themeImages = getThemeImages(theme.name);
  const withNegativeSignForNegativeAmount = value < 0 && !withBillNegativeAmount;

  return (
    <Card
      onPressCard={() => onPress({ isStartDateBillDate, year, month, timePeriod })}
      style={[styles.cardStyle(theme), { cursor: 'pointer' }]}
    >
      <VfView justify="center" alignItems="center" testID={billCardTestID}>
        <VfView xs={Platform.OS === 'web' && Dimensions.get('window').width > 700 ? 12 : 11}>
          {isLoading ? (
            Loader
          ) : (
            <>
              <>
                <VfView row justify="space-between" alignItems="center">
                  <VFText
                    style={styles.yearTextStyle(theme)}
                    i18nKey={withTimePeriodDisplayed ? `${year} | ${timePeriod}` : `${year}`}
                    testID={BillingTestKeys.billCardYearText}
                  />
                  {tagText ? (
                    <Badge
                      text={tagText}
                      style={tagContainerStyle}
                      textStyle={tagTextStyle}
                      testKey={BillingTestKeys.billCardBadge}
                    />
                  ) : (
                    <Tag
                      status={status}
                      containerStyle={tagContainerStyle}
                      textStyle={tagTextStyle}
                    />
                  )}
                </VfView>
                <VfView
                  row
                  justify="space-between"
                  alignItems="center"
                  style={styles.monthStatusContainer}
                  testID={BillingTestKeys.billCardMonthStatusContainer}
                >
                  <VFText
                    i18nKey={month}
                    style={styles.monthTextStyle(theme)}
                    testID={monthTestID}
                  />
                  <VfView row alignItems="center" justify="center">
                    <VFText
                      i18nKey={getBillAmountWithCurrencyFirst(
                        isCurrencyFirst,
                        withBillNegativeAmount,
                        value,
                        currency,
                      )}
                      style={[
                        styles.priceTextStyle(theme),
                        getBillAmountStyle(withNegativeSignForNegativeAmount, billAmountStyle),
                      ]}
                      testKey={priceTestID}
                    />
                    <VFText
                      i18nKey={getBillAmountWithAmountFirst(
                        isCurrencyFirst,
                        withBillNegativeAmount,
                        value,
                        currency,
                      )}
                      style={[
                        styles.currencyTextStyle(theme),
                        getBillAmountStyle(withNegativeSignForNegativeAmount, billAmountStyle),
                      ]}
                      testKey={amountTestID}
                    />
                    {withNegativeSignForNegativeAmount && (
                      <TouchableOpacity
                        onPress={onNegativeBillIconPress}
                        activeOpacity={1}
                        hitSlop={{
                          top: 10,
                          left: 10,
                          bottom: 10,
                          right: 10,
                        }}
                        testID={BillingTestKeys.billCardNegativeBillTO}
                      >
                        <VfView
                          style={styles.negativeBalanceContainer}
                          row
                          testID={BillingTestKeys.billCardNegativeBillView}
                        >
                          <Icon
                            source={themeImages.icInfoRed}
                            type={IconTypes.image}
                            size={22}
                            style={styles.icon}
                            testID={BillingTestKeys.billCardNegativeBillIcon}
                          />
                        </VfView>
                      </TouchableOpacity>
                    )}
                  </VfView>
                </VfView>
                {withNegativeSignForNegativeAmount && (
                  <VfView justify-content="center" alignItems="flex-end">
                    <VFText
                      i18nKey={billingNegativeValueNote}
                      style={billingNegativeValueNoteStyle}
                      testKey={BillingTestKeys.billCardNegativeBillValueNote}
                    />
                  </VfView>
                )}
                {((paidValue && status === 'partiallyPaid') ||
                  descriptionFirstLine ||
                  descriptionSecondLine) && (
                    <VfView
                      row
                      justify="flex-end"
                      style={styles.remainingAmountStyle}
                      testID={BillingTestKeys.billCardRemainingAmountText}
                    >
                      <VfView>
                        <VFText
                          i18nKey={descriptionFirstLine || remainingAmountText[0]}
                          style={[styles.remainingAmountTextStyle(theme), descriptionFirstLineStyle]}
                          testKey={BillingTestKeys.billCardDescriptionAmountFirstLineText}
                        />
                        <VFText
                          i18nKey={descriptionSecondLine || `(${remainingAmountText[1]}`}
                          style={[styles.remainingAmountTextStyle(theme), descriptionSecondLineStyle]}
                          testKey={BillingTestKeys.billCardDescriptionAmountSecondLineText}
                        />
                      </VfView>
                    </VfView>
                  )}
                {status === 'new' && !!unPaidCardDescriptionText && (
                  <VfView
                    row
                    justify="flex-end"
                    style={styles.remainingAmountStyle}
                    testID={BillingTestKeys.billCardUnPaidCardDescriptionContainer}
                  >
                    <VFText
                      i18nKey={unPaidCardDescriptionText}
                      style={unPaidCardDescriptionTextStyle}
                      placeHolders={unPaidCardDescriptionTextPlaceholders}
                      testKey={BillingTestKeys.billCardUnPaidCardDescriptionText}
                    />
                  </VfView>
                )}
              </>
              {_isFunction(renderFooter) ? (
                <VfView style={styles.lineTop(theme)}>{renderFooter()}</VfView>
              ) : (
                <>
                  {imageListProp && (
                    <VfView style={styles.lineTop(theme)}>
                      <ImageList
                        showTitle={withServicesExtraLabel}
                        images={images}
                        title={servicesExtraLabel}
                        {...imageListProp}
                      />
                    </VfView>
                  )}
                </>
              )}
            </>
          )}
        </VfView>
      </VfView>
    </Card>
  );
}

BillCard.defaultProps = {
  getYear: DateFormatters.getYear,
  getMonth: DateFormatters.getMonth,
  getTimePeriod: DateFormatters.getTimePeriod,
  isStartDateBillDate: false,
  theme: LightThemeColors,
  withTimePeriodDisplayed: false,
  withServicesExtraLabel: false,
  tagText: null,
  unPaidCardDescriptionText: '',
  unPaidCardDescriptionTextStyle: {},
  unPaidCardDescriptionTextPlaceholders: [],
  priceTestID: BillingTestKeys.billCardPriceText,
};

BillCard.propTypes = {
  status: PropTypes.oneOf(['sent', 'new', 'onHold', 'partiallyPaid', 'settled', 'validated']),
  isLoading: PropTypes.bool,
  unPaidCardDescriptionText: PropTypes.string,
  unPaidCardDescriptionTextStyle: TextPropTypes.style,
  unPaidCardDescriptionTextPlaceholders: PropTypes.array,
  loader: PropTypes.node,
  descriptionFirstLine: PropTypes.string,
  descriptionFirstLineStyle: ViewPropTypes.style,
  descriptionSecondLine: PropTypes.string,
  descriptionSecondLineStyle: ViewPropTypes.style,
  renderFooter: PropTypes.func,
  imageListProp: PropTypes.object,
  onPress: PropTypes.func,
  isCurrencyFirst: PropTypes.bool,
  value: PropTypes.number,
  currency: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  getYear: PropTypes.func,
  getMonth: PropTypes.func,
  getTimePeriod: PropTypes.func,
  isStartDateBillDate: PropTypes.bool,
  paidValue: PropTypes.number,
  theme: PropTypes.object,
  monthTestID: PropTypes.string,
  amountTestID: PropTypes.string,
  priceTestID: PropTypes.string,
  withTimePeriodDisplayed: PropTypes.bool,
  withServicesExtraLabel: PropTypes.bool,
  servicesExtraLabel: PropTypes.string,
  billCardTestID: PropTypes.string,
  issueDate: PropTypes.string,
  tagContainerStyle: ViewPropTypes.style,
  tagTextStyle: TextPropTypes.style,
  tagText: PropTypes.string,
  valueLocale: PropTypes.string,
  toggleModal: PropTypes.func,
  negativeBillIconInfo: PropTypes.string,
  withBillNegativeAmount: PropTypes.bool,
  billAmountStyle: PropTypes.object,
  billingNegativeValueNote: PropTypes.string,
  billingNegativeValueNoteStyle: PropTypes.object,
};

export default withTheme(BillCard);
export { getDateFormatters };
